/**
 * 学习乐园 · 序列记忆（game-xulie.js）
 * ============================================================
 * 「记忆与观察」组第四作：色垫依次点亮（伴音），照原顺序点回来，每过一轮加一步。
 * Simon 式工作记忆训练——顺序保持 + 声像绑定（每垫一个固定音高）。
 *
 * 档位（默认档铁律——最简）：
 *   入门 4 垫，从 3 步起，每轮 +1，上限 8 步，回放 650ms/步（默认档）
 *   进阶 6 垫，从 4 步起，上限 12 步，500ms/步
 *   挑战 6 垫，从 5 步起，上限 16 步，380ms/步
 *
 * 反馈口径：点错不惩罚不重来——明说「第 x 步应该是某垫，你点了某垫」，
 * 然后用同一序列重放本轮（不换题，盯着看明白为止）。
 *
 * 音色：WebAudio 本地合成，4 垫 = C4/E4/G4/C5，6 垫 = C4/D4/E4/F4/G4/A4；
 * 顺带练耳感（音高随垫位固定，听声也能记序）。
 *
 * 记录：按档位记通关次数（走满上限）与最佳步数（最长走到的序列长度），只存本机。
 *
 * 冒烟钩子：window.STXulie 暴露 makeSeq / growSeq / LEVELS / setPace(ms)（仅测试加速用）
 * / state()。fixRandom 下 Math.floor(Math.random()*pads) 恒 0 → 序列全 0 号垫。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-xulie';
  var RECORD_KEY = 'study-xulie-record';

  /* ============ 引擎：出题（纯函数） ============ */

  // 生成 len 步序列，每步 0..pads-1（确定性：fixRandom 下恒选 0 号垫）
  function makeSeq(len, pads) {
    var seq = [];
    for (var i = 0; i < len; i++) seq.push(Math.floor(Math.random() * pads));
    return seq;
  }

  // 过轮推进：保留前缀、只追加一步（Simon 本义——记过的步不白记，每轮只多记一步；
  // 独立成纯函数便于单测守住「不换题」口径）
  function growSeq(seq, pads) {
    var next = seq.slice();
    next.push(Math.floor(Math.random() * pads));
    return next;
  }

  /* ============ UI：挂载与状态 ============ */

  var LEVELS = [
    { pads: 4, start: 3, max: 8, stepMs: 650, name: '入门 4 垫', hint: '默认档 · 3 步起' },
    { pads: 6, start: 4, max: 12, stepMs: 500, name: '进阶 6 垫', hint: '4 步起' },
    { pads: 6, start: 5, max: 16, stepMs: 380, name: '挑战 6 垫', hint: '5 步起 · 快回放' }
  ];
  // 垫色（明暗两主题都可辨 + 明度差辅助色弱辨识）与音高（Hz）
  var PAD_COLORS = ['#e05c5c', '#dfa32e', '#4caf6d', '#4f8fd9', '#e0853e', '#9a6fd0'];
  var PAD_NAMES = ['红', '黄', '绿', '蓝', '橙', '紫'];
  var FREQ4 = [261.6, 329.6, 392.0, 523.3];               // C4 E4 G4 C5
  var FREQ6 = [261.6, 293.7, 329.6, 349.2, 392.0, 440.0]; // C4 D4 E4 F4 G4 A4

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var root = null;
  var ui = {};
  var S = {
    soundOn: true,
    levelIdx: 0,
    seq: [],             // 当前轮序列
    round: 1,            // 第几轮（步长 = start + round - 1）
    inputAt: 0,          // 已点对几步
    phase: 'show',       // show（回放）| input（点回）| ended
    tries: 0,            // 本轮重试次数
    paceMs: 0,           // 冒烟加速覆盖（0 = 用档位默认）
    showTimers: [],
    ended: false,
    resetArm: false, resetTimer: null
  };

  function level() { return LEVELS[S.levelIdx]; }
  function stepMs() { return S.paceMs || level().stepMs; }

  /* ---- 档位选择器 ---- */

  function renderLevelButtons() {
    var box = ui.levels;
    box.textContent = '';
    for (var i = 0; i < LEVELS.length; i++) {
      (function (lv, idx) {
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-level', String(idx));
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', lv.name));
        b.appendChild(el('small', 'st-llk-pack-hint', lv.hint));
        b.addEventListener('click', function () {
          if (S.levelIdx === idx) return;
          S.levelIdx = idx;
          newGame();   // 换档 = 直接开新局
        });
        box.appendChild(b);
      })(LEVELS[i], i);
    }
  }

  function syncLevelButtons() {
    var btns = ui.levels.querySelectorAll('[data-level]');
    for (var i = 0; i < btns.length; i++) {
      var on = Number(btns[i].getAttribute('data-level')) === S.levelIdx;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- 色垫 ---- */

  function buildPads() {
    var lv = level();
    var box = ui.pads;
    box.textContent = '';
    box.style.setProperty('--st-xl-cols', lv.pads > 4 ? 3 : 2);
    ui.padBtns = [];
    for (var i = 0; i < lv.pads; i++) {
      (function (idx) {
        var b = el('button', 'st-xl-pad');
        b.type = 'button';
        b.style.setProperty('--pad-c', PAD_COLORS[idx]);
        b.setAttribute('data-pad', String(idx));
        b.setAttribute('aria-label', PAD_NAMES[idx] + '垫');
        b.addEventListener('click', function () { onPad(idx); });
        box.appendChild(b);
        ui.padBtns.push(b);
      })(i);
    }
    renderPads();
  }

  function renderPads() {
    for (var i = 0; i < (ui.padBtns || []).length; i++) {
      var b = ui.padBtns[i];
      b.classList.toggle('is-lit', b.__lit === true);
      b.disabled = S.phase !== 'input';
    }
  }

  /* ---- 渲染：状态 / 反馈 / 成绩 / 按钮 ---- */

  function renderStatus() {
    if (S.ended) return;
    ui.status.className = 'st-status';
    if (S.phase === 'show') {
      ui.status.textContent = '看它亮：第 ' + S.round + ' 轮 ' + S.seq.length + ' 步，照原样点回来';
    } else {
      ui.status.textContent = '第 ' + S.round + ' 轮（' + S.seq.length + ' 步）· 已点对 '
        + S.inputAt + '/' + S.seq.length
        + (S.tries ? ' · 重试 ' + S.tries : '');
    }
  }

  function renderNote(text, tone) {
    ui.note.textContent = text || '';
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function recKey() { return 'l' + S.levelIdx; }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord()[recKey()];
    if (!rec || (!rec.wins && !rec.best)) {
      box.className = 'st-llk-record is-empty';
      box.appendChild(el('p', 'st-llk-record-empty',
        '这一档还没走过——从 ' + level().start + ' 步开始一轮轮加长，这里记下你走过的最长序列。'));
      return;
    }
    box.className = 'st-llk-record';
    box.appendChild(recordStat('通关', (rec.wins || 0) + ' 次（' + level().max + ' 步走满）'));
    box.appendChild(recordStat('最佳步数', rec.best ? rec.best + ' 步' : '—'));
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  function renderButtons() {
    ui.newBtn.disabled = false;
    ui.resetBtn.disabled = false;
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  /* ---- 音效：每垫固定音高（顺带练耳感）+ 通用提示音 ---- */

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function tone(freq, at, dur) {
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime + at;
      var o = actx.createOscillator();
      var g = actx.createGain();
      o.type = 'triangle';
      o.frequency.setValueAtTime(SFX_MOBILE && freq < 500 ? freq * 2 : freq, t);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.2, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur + (SFX_MOBILE ? 0.06 : 0));
      o.connect(g); g.connect(actx.destination);
      o.start(t); o.stop(t + dur + (SFX_MOBILE ? 0.11 : 0.05));
    } catch (e) { /* 音频不可用静默 */ }
  }
  function padFreq(pad) {
    var lv = level();
    var freqs = lv.pads > 4 ? FREQ6 : FREQ4;
    return freqs[pad % freqs.length];
  }
  function sfx(kind) {
    if (!S.soundOn) return;
    if (kind === 'win') { [523, 659, 784, 1046].forEach(function (f, i) { tone(f, i * 0.1, 0.16); }); }
    else if (kind === 'bad') { tone(196, 0, 0.16); tone(185, 0.09, 0.16); }
  }

  /* ---- 回放：按步长依次点亮色垫（伴音） ---- */

  function clearShowTimers() {
    for (var i = 0; i < S.showTimers.length; i++) clearTimeout(S.showTimers[i]);
    S.showTimers = [];
    for (var j = 0; j < (ui.padBtns || []).length; j++) ui.padBtns[j].__lit = false;
  }

  function playSeq() {
    clearShowTimers();
    S.phase = 'show';
    S.inputAt = 0;
    renderPads();
    renderStatus();
    renderNote('第 ' + S.round + ' 轮 ' + S.seq.length + ' 步，看仔细、听着拍子——回放完照原样点回来。', 'warn');
    var ms = stepMs();
    S.seq.forEach(function (pad, i) {
      S.showTimers.push(setTimeout(function () {
        var b = ui.padBtns[pad];
        if (!b) return;
        b.__lit = true;
        renderPads();
        if (S.soundOn) tone(padFreq(pad), 0, Math.min(0.3, ms / 1000 * 0.7));
        S.showTimers.push(setTimeout(function () {
          b.__lit = false;
          renderPads();
        }, ms * 0.55));
      }, ms * i + 350));   // 起手留 350ms 缓冲
    });
    S.showTimers.push(setTimeout(function () {
      S.phase = 'input';
      renderPads();
      renderStatus();
      renderNote('该你了：' + S.seq.length + ' 步照原样点。错了不重来，页面会说是哪一步、该点哪个垫。', '');
    }, ms * S.seq.length + 400));
  }

  /* ---- 交互：点垫 ---- */

  function onPad(idx) {
    if (S.ended || S.phase !== 'input') return;
    var want = S.seq[S.inputAt];
    if (S.soundOn) tone(padFreq(idx), 0, 0.18);
    if (idx === want) {
      S.inputAt++;
      renderStatus();
      if (S.inputAt >= S.seq.length) {
        if (S.seq.length >= level().max) { winGame(); return; }
        // 本轮走完：保留前缀、追加一步（Simon 本义），下一轮回放的是原序列 +1 步
        S.round++;
        S.seq = growSeq(S.seq, level().pads);
        renderNote('全对！下一轮 ' + S.seq.length + ' 步——序列越来越长，工作记忆就是这样练起来的。', 'ok');
        S.showTimers.push(setTimeout(playSeq, 900));
        S.phase = 'show';
        renderPads();
        renderStatus();
      }
    } else {
      // 点错：讲清错在哪一步、该点哪个垫，然后同一序列重放（不换题不惩罚）
      S.tries++;
      sfx('bad');
      S.phase = 'show';
      renderPads();
      renderStatus();
      renderNote('第 ' + (S.inputAt + 1) + ' 步应该是「' + PAD_NAMES[want] + '」，你点了「'
        + PAD_NAMES[idx] + '」。别急，还是这 ' + S.seq.length
        + ' 步——盯住刚才错的那一步，再来一遍。', 'bad');
      S.showTimers.push(setTimeout(playSeq, 1300));
    }
  }

  /* ---- 通关与成绩（只存本机） ---- */

  function winGame() {
    S.ended = true;
    S.phase = 'ended';
    clearShowTimers();
    renderPads();
    sfx('win');
    var rec = loadRecord();
    var key = recKey();
    var cur = rec[key];
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) cur = { wins: 0, best: 0 };
    if (typeof cur.wins !== 'number') cur.wins = 0;
    if (typeof cur.best !== 'number') cur.best = 0;
    cur.wins++;
    var isRec = !cur.best || S.seq.length > cur.best;
    if (S.seq.length > cur.best) cur.best = S.seq.length;
    rec[key] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 隐私模式静默 */ }
    ui.status.className = 'st-status is-over';
    ui.status.textContent = '通关！' + level().max + ' 步走满，重试 ' + S.tries
      + ' 次' + (isRec ? '——这一档的新纪录！' : '（你这档最佳 ' + cur.best + ' 步）');
    renderNote('记长序列的诀窍还是组块化：把 16 步拆成 4×4 的小段，按「形状」记（比如红绿红蓝算一组），一段一段背。', 'ok');
    renderRecord();
    renderButtons();
  }

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try { localStorage.removeItem(RECORD_KEY); } catch (e) { /* 隐私模式静默 */ }
    renderRecord();
    renderButtons();
    toast('已重置序列记忆的本机成绩');
  }

  function save() {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, levelIdx: S.levelIdx, soundOn: S.soundOn }));
    } catch (e) { /* 存储不可用静默 */ }
  }

  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (!d || d.v !== 1) return;
      if (d.levelIdx >= 0 && d.levelIdx < LEVELS.length) S.levelIdx = d.levelIdx;
      S.soundOn = d.soundOn !== false;
    } catch (e) { /* 忽略脏数据 */ }
  }

  function newGame() {
    clearShowTimers();
    S.seq = makeSeq(level().start, level().pads);
    S.round = 1;
    S.inputAt = 0;
    S.tries = 0;
    S.ended = false;
    S.resetArm = false;
    syncLevelButtons();
    buildPads();
    renderStatus();
    renderButtons();
    renderRecord();
    playSeq();
    save();
  }

  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }

  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.levels = root.querySelector('[data-st-xl-levels]');
    ui.pads = root.querySelector('[data-st-xl-pads]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.record = root.querySelector('[data-st-record]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.resetBtn = root.querySelector('[data-st-reset]');
    if (!ui.pads) return;

    restorePrefs();
    renderLevelButtons();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(); });
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame();
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STXulie = {
    makeSeq: makeSeq,
    growSeq: growSeq,
    LEVELS: LEVELS,
    // 冒烟/自动化测试专用：把回放节奏压到给定毫秒（产品默认节奏不受影响）
    setPace: function (ms) { S.paceMs = Math.max(1, Number(ms) || 0); },
    state: function () {
      return {
        levelIdx: S.levelIdx, pads: level().pads, start: level().start, max: level().max,
        seq: S.seq.slice(), round: S.round, inputAt: S.inputAt,
        phase: S.phase, tries: S.tries, ended: S.ended
      };
    }
  };
})(typeof window !== 'undefined' ? window : globalThis);

